import {
  deleteUpload,
  getPresignedUrl,
  getS3ObjectUrl,
  uploadToS3,
} from '@/api/uploads';
import { StagedFile } from '@/features/ticket/components/TicketDetailsDialog/tabs/PhotoUploadTab';
import { useAuth } from '@/providers/AuthProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export const useS3TicketUploads = (ticketId: string) => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;
  /**
   * Mutation to handle the upload of a single file.
   * It gets a presigned URL, uploads the file, and returns key info.
   */
  const uploadMutation = useMutation({
    mutationFn: async (stagedFile: StagedFile) => {
      const { uploadUrl, viewUrl } = await getPresignedUrl(
        ticketId,
        stagedFile.file,
      );

      await uploadToS3(uploadUrl, stagedFile.file);

      return { ...stagedFile, viewUrl };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['tickets', ticketId, 'uploads', orgId],
      });
    },
    onError: (error, stagedFile) => {
      toast.error(`Upload failed for ${stagedFile.file.name}`);
      console.error(`[Upload Error] ${stagedFile.file.name}:`, error);
    },
  });

  const viewPhotoMutation = useMutation({
    mutationFn: (uploadId: string) => getS3ObjectUrl(uploadId),
    onSuccess: (data) => {
      // On success, open the retrieved URL in a new browser tab.
      if (data?.viewUrl) {
        window.open(data.viewUrl, '_blank', 'noopener,noreferrer');
      }
    },
    onError: (error) => {
      toast.error('Could not retrieve image. Please try again.');
      console.error('Failed to get view URL:', error);
    },
  });

  const deletePhotoMutation = useMutation({
    mutationFn: (uploadId: string) => deleteUpload(uploadId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['tickets', ticketId, 'uploads', orgId],
      });
      toast.success('Upload deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete upload.');
      console.error('Failed to delete upload:', error);
    },
  });

  return {
    uploadMutation,
    viewPhotoMutation,
    deletePhotoMutation,
  };
};
